import {deepCopyObject} from '@/utils/portuna';
import localforage from 'localforage';
import {reactive} from 'vue';

const temporaryStore = localforage.createInstance({
    name: 'portunaStorage',
    storeName: 'temporary',
});

interface LocalStorageListeners
{
    [index: string]: LocalStorageCallback[];
}

class LocalStorageWrapper
{
    /** all listener callbacks */
    private _listeners: LocalStorageListeners = {};

    private _data: Record<string, any> = {};

    private readonly _broadcast: Nullable<BroadcastChannel> = null;

    constructor()
    {
        // prefetch storage and publish internal object
        temporaryStore.keys().then((keys) =>
        {
            for (let key of keys)
            {
                temporaryStore.getItem(key).then((value) =>
                {
                    this._data[key] = value;
                });
            }
        });

        try
        {
            this._broadcast           = new BroadcastChannel('storage_update');
            this._broadcast.onmessage = (event) =>
            {
                const key = event.data;
                if (Array.isArray(this._listeners[key]))
                {

                    temporaryStore.getItem(key).then((value: any) =>
                    {
                        if (this._data[key] !== value)
                        {
                            let oldValue = this._data[key];
                            this._data[key] = value;

                            for (let callback of this._listeners[key])
                            {
                                callback({
                                    key,
                                    newValue: value,
                                    oldValue: oldValue,
                                });
                            }
                        }
                    });
                }
            };
        }
        catch (e)
        {
            // nothing to do -> localstorage fallback next
        }

        if (!this._broadcast && 'addEventListener' in window)
        {
            // on local storage update
            window.addEventListener('storage', (event) =>
            {
                let key = event && event.key || '';
                if (key)
                {
                    if (key === 'crossTabCommunication')
                    {
                        key = event.newValue || '';
                        // storage was removed - nothing to do
                        if (!key)
                        {
                            return;
                        }
                    }

                    if (Array.isArray(this._listeners[key]))
                    {
                        temporaryStore.getItem(key).then((value: any) =>
                        {
                            if (this._data[key] !== value)
                            {
                                let oldValue = this._data[key];
                                this._data[key] = value;

                                for (let callback of this._listeners[key])
                                {
                                    callback({
                                        key,
                                        newValue: value,
                                        oldValue,
                                    });
                                }
                            }
                        });

                    }
                }
            });
        }

        reactive(this._data);
    }

    /**
     * set the `key` to `value` - overwrite if already exists
     *
     * @param {string} key used key
     * @param {*} value value for `key`
     * @returns {}
     */
    set(key: string, value: any)
    {
        if ('object' === typeof value)
        {
            // filter proxies for arrays/objects
            value = deepCopyObject(value);
        }

        this._data[key] = value;
        temporaryStore.setItem(key, value).then(() =>
        {
            if (this._broadcast)
            {
                this._broadcast.postMessage(key);
            }
            // no broadcast. use localstorage api
            else
            {
                window.localStorage.setItem('crossTabCommunication', key);
                // can be removed directly.
                //+ `window.addEventListener('storage', ...)` is still triggered
                window.localStorage.removeItem('crossTabCommunication');
            }
        });

        return this;
    }

    /**
     * get the value of `key`
     *
     * @param {string} key used key
     * @param {*} [defaultValue] default if `key` doesn't exists in storage
     * @returns {*}
     */
    get(key: string, defaultValue: any = null): any
    {
        if ('undefined' === typeof this._data[key] || (null === this._data[key] && null !== defaultValue))
        {
            this._data[key] = defaultValue;
        }

        return this._data[key];
    }

    /**
     * delete the key
     *
     * @param {string} key used key
     * @returns {boolean}
     */
    unset(key: string)
    {
        temporaryStore.removeItem(key).then();

        return this;
    }

    clear()
    {
        temporaryStore.clear().then();
        for(const key in this._data)
        {
            if(this._data.hasOwnProperty(key))
            {
                delete this._data[key];
            }
        }
        return this;
    }

    on(key: string, fn: LocalStorageCallback)
    {
        if (typeof this._listeners[key] === 'undefined')
        {
            this._listeners[key] = [];
        }

        this._listeners[key].push(fn);
    }

    off(key: string, fn: LocalStorageCallback)
    {
        if (typeof this._listeners[key] === 'undefined')
        {
            return;
        }

        let index = this._listeners[key].indexOf(fn);
        if (index >= 0)
        {
            this._listeners[key].splice(index, 1);
        }
    }
}

const LocalStorage = new LocalStorageWrapper();
export default LocalStorage;